import * as React from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { useMe } from "@/api/me";
import { Brandmark } from "@/components/layout/Brandmark";
import { NAV_GROUPS, filterNav, type NavItem } from "@/components/layout/nav";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/authStore";
import { useUIStore } from "@/store/uiStore";

/**
 * The grouped rail (MV2-845, app/ShellOM · ShellClientAdmin · ShellSuperAdmin,
 * ds/Nav, app/ShellDetails).
 *
 * 196px expanded, 56px collapsed, on `--surface-sunk`. Four verb groups with
 * a 9.5px eyebrow (a hairline when collapsed); 32px items, 13px labels; the
 * active item is gray-900 with a 3px `--action` bar on the left — never a
 * fill. The tenant's mark and app_name sit at the top; Collapse at the foot.
 * Collapsed, a portalled bubble names the item and its `g` shortcut.
 */
export function Sidebar(): React.ReactElement {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const { data: me } = useMe();
  const persona = me?.primary_persona ?? null;
  const actingClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  const isImpersonating = actingClientId != null;
  const features = me?.features ?? null;
  const groups = React.useMemo(
    () => filterNav(NAV_GROUPS, persona, isImpersonating, features),
    [persona, isImpersonating, features],
  );

  const [peek, setPeek] = React.useState<PeekState | null>(null);
  React.useEffect(() => {
    if (!collapsed) setPeek(null);
  }, [collapsed]);
  const clearPeek = React.useCallback(() => setPeek(null), []);

  return (
    <aside
      className={cn(
        "flex h-full flex-none flex-col border-r border-gray-200 bg-surface-sunk pb-2.5 pt-3 transition-[width] duration-enter ease-standard motion-reduce:transition-none",
        collapsed ? "w-14" : "w-[196px]",
      )}
      aria-label="Primary navigation"
    >
      <div
        className={cn(
          // Expanded: the full wordmark, centred, scaled to the rail's width
          // (Kranthi, 8 Sep). Collapsed: the 24px mark.
          "flex h-10 items-center justify-center pb-3",
          collapsed ? "px-4" : "px-3.5",
        )}
      >
        <Brandmark
          rail
          withName={!collapsed}
          branding={me?.branding ?? null}
          loading={me === undefined}
          className="w-full justify-center"
        />
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden">
        {groups.map((g, gi) => (
          <div key={g.group} className="flex flex-col gap-px pb-2">
            {collapsed ? (
              gi > 0 && (
                <div
                  aria-hidden
                  className="mx-3.5 mb-1.5 mt-1 h-px bg-gray-200"
                />
              )
            ) : (
              <div className="type-eyebrow px-3.5 pb-1 pt-1.5 text-[9.5px]">
                {g.group}
              </div>
            )}
            <ul className="flex flex-col gap-px">
              {g.items.map((item) => (
                <RailItem
                  key={item.to}
                  item={item}
                  collapsed={collapsed}
                  onPeek={setPeek}
                  onPeekEnd={clearPeek}
                />
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div
        className={cn(
          "border-t border-gray-200 pt-2",
          collapsed ? "flex justify-center" : "px-3.5",
        )}
      >
        <button
          type="button"
          onClick={toggleSidebar}
          className={cn(
            "flex h-7 items-center gap-[9px] rounded-md text-gray-500 transition-colors duration-hover hover:text-gray-900",
            collapsed ? "w-7 justify-center" : "w-full",
          )}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand (⌘\\)" : "Collapse (⌘\\)"}
        >
          {collapsed ? (
            <PanelLeftOpen size={15} />
          ) : (
            <PanelLeftClose size={15} />
          )}
          {!collapsed && <span className="type-meta">Collapse</span>}
        </button>
      </div>

      <RailTooltip peek={peek} />
    </aside>
  );
}

/**
 * Collapsed-rail bubble (MV2-738, restated on app/ShellDetails): portalled
 * and fixed because the nav scrolls, so an absolute bubble would clip at the
 * 56px edge. Names the item and its shortcut. One instance for the rail.
 */
interface PeekState {
  label: string;
  key?: string;
  x: number;
  y: number;
}

function RailTooltip({
  peek,
}: {
  peek: PeekState | null;
}): React.ReactElement | null {
  if (!peek) return null;
  return createPortal(
    <div
      role="presentation"
      className="pointer-events-none fixed z-[1200] flex -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-[8px] border border-gray-200 bg-surface px-2.5 py-1.5 text-[12px] text-gray-900 shadow-md"
      style={{ left: peek.x, top: peek.y }}
    >
      <b className="font-semibold">{peek.label}</b>
      {peek.key && (
        <span className="font-mono text-[10.5px] text-gray-500">
          g {peek.key}
        </span>
      )}
    </div>,
    document.body,
  );
}

function RailItem({
  item,
  collapsed,
  onPeek,
  onPeekEnd,
}: {
  item: NavItem;
  collapsed: boolean;
  onPeek: (peek: PeekState) => void;
  onPeekEnd: () => void;
}): React.ReactElement {
  const Icon = item.icon;
  const peek = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      if (!collapsed) return;
      const r = e.currentTarget.getBoundingClientRect();
      onPeek({
        label: item.label,
        key: item.key,
        x: r.right + 8,
        y: r.top + r.height / 2,
      });
    },
    [collapsed, item.label, item.key, onPeek],
  );

  return (
    <li>
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          cn(
            "relative flex h-8 items-center gap-2.5 text-[13px] transition-colors duration-hover motion-reduce:transition-none",
            collapsed ? "justify-center px-0" : "px-3.5",
            isActive
              ? "font-medium text-gray-900"
              : "text-gray-500 hover:bg-gray-100/70 hover:text-gray-900",
          )
        }
        aria-label={collapsed ? item.label : undefined}
        onMouseEnter={peek}
        onMouseLeave={onPeekEnd}
        onFocus={peek}
        onBlur={onPeekEnd}
        onClick={onPeekEnd}
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden
                className="absolute bottom-1.5 left-0 top-1.5 w-[3px] rounded-r-[2px] bg-action"
              />
            )}
            <Icon size={16} />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </>
        )}
      </NavLink>
    </li>
  );
}
